import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axios';
import compressImage from '../utils/imageCompression';
import AppShell from '../components/AppShell';
import InspectionItemRow from '../components/InspectionItemRow';
import SaveModal from '../components/SaveModal';
import { DEFAULT_GENERIC_RESULT_OPTIONS, getResultLookupType, normalizeResultOptions } from '../utils/resultOptions';

export default function SectionInspection({ section, title, saveEndpoint, saveEndpointPath }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [values, setValues] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [inspectionState, setInspectionState] = useState(null);
  const [resultOptions, setResultOptions] = useState(DEFAULT_GENERIC_RESULT_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const errorText = (err) => {
    if (err?.code === 'ECONNABORTED' || String(err?.message || '').includes('timeout')) {
      return 'الخادم استغرق وقتًا أطول من المتوقع. حاول مرة أخرى.';
    }
    return err?.response?.data?.message || 'حدث خطأ أثناء تحميل بيانات الفحص.';
  };

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      setLoading(true);
      setError('');
      try {
        const [itemsRes, inspectionRes, lookupRes] = await Promise.all([
          api.get(`/inspection-items/by-section/${section}`),
          api.get(`/inspections/${id}`),
          api.get(`/lookups/by-type/${getResultLookupType('generic')}`).catch(() => ({ data: [] })),
        ]);

        if (!mounted) return;
        setItems(itemsRes.data || []);
        setInspectionState(inspectionRes.data || null);
        setResultOptions(normalizeResultOptions(lookupRes.data, 'generic'));

        const mapped = {};
        (inspectionRes.data?.inspection_results || []).forEach((result) => {
          if (!result?.inspection_item_id) return;
          const resultSection = result?.inspection_item?.section || result?.inspectionItem?.section;
          if (resultSection !== section) return;
          mapped[result.inspection_item_id] = {
            item_id: result.inspection_item_id,
            condition_type: result.condition_type || '',
            notes: result.notes || '',
          };
        });
        (inspectionRes.data?.attachments || []).forEach((attachment) => {
          if (attachment?.section !== section || !attachment?.inspection_item_id) return;
          mapped[attachment.inspection_item_id] = {
            ...(mapped[attachment.inspection_item_id] || {}),
            item_id: attachment.inspection_item_id,
            attachment,
          };
        });
        setValues(mapped);
      } catch (err) {
        if (!mounted) return;
        setError(errorText(err));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [section, id]);

  const setItemValue = (itemId, payload) => {
    setValues((prev) => ({ ...prev, [itemId]: { ...prev[itemId], ...payload, item_id: itemId } }));
  };

  const upload = async (itemId, rawFile) => {
    if (!rawFile) return;
    const file = await compressImage(rawFile);
    const form = new FormData();
    const currentValue = values[itemId] || {};
    form.append('file', file);
    form.append('section', section);
    form.append('item_id', itemId);
    form.append('condition_type', currentValue.condition_type || '');
    form.append('notes', currentValue.notes || '');
    try {
      const response = await api.post(`/inspections/${id}/attachments`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      setItemValue(itemId, { attachment: response.data });
    } catch (err) {
      setError(errorText(err));
    }
  };

  const removeImage = async (itemId, attachment) => {
    if (!attachment?.id) {
      setItemValue(itemId, { attachment: null });
      return;
    }

    try {
      await api.delete(`/inspections/${id}/attachments/${attachment.id}`);
      setItemValue(itemId, { attachment: null });
    } catch (err) {
      setError(errorText(err));
    }
  };

  const payloadItems = useMemo(() => Object.values(values), [values]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await api.post(`/inspections/${id}/${saveEndpointPath || saveEndpoint}`, { items: payloadItems });
      const res = await api.get(`/inspections/${id}`);
      setInspectionState(res.data);
      setShowModal(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell
      title={title}
      actions={
        <button className="px-3 py-2 rounded-lg border" onClick={() => navigate(`/inspection/${id}/menu`)}>
          رجوع للقائمة
        </button>
      }
    >
      {loading && (
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-3 text-blue-700 text-sm">
          جاري تحميل بنود الفحص...
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((item) => (
          <InspectionItemRow
            key={item.id}
            item={item}
            value={values[item.id]}
            onChange={setItemValue}
            onUpload={upload}
            onRemoveImage={removeImage}
            resultOptions={resultOptions}
          />
        ))}
      </div>

      <div className="mt-6">
        <button
          onClick={save}
          disabled={saving || loading}
          className="rounded-xl bg-primary px-6 py-3 text-white font-bold disabled:opacity-50"
        >
          {saving ? 'جاري الحفظ...' : 'حفظ'}
        </button>
      </div>

      <SaveModal
        open={showModal}
        onClose={() => setShowModal(false)}
        inspectionId={id}
        state={inspectionState}
      />
    </AppShell>
  );
}
